class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.isHeaderSearch = this.closest('header') !== null;
    this.headerSearchWrapper = this.closest('.header__search');
    this.mobileSearchTrigger =
      this.headerSearchWrapper?.nextElementSibling?.querySelector('.search-icon-mobile') ?? null;

    this._brLastPointerDownAt = 0;
    this._brLastPointerDownTarget = null;
    this._mobileSearchScrollWindowListener = false;
    this._mobileSearchScrollRaf = null;
    this._mobileSearchScrollGraceUntil = 0;
    this._autoMobileSearchScrollRaf = null;
    this._autoMobileSearchEnabled = false;

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundOnBrPointerDown = this.onBrPointerDown.bind(this);
    this.boundOnBrViewportResize = this.onBrViewportResize.bind(this);
    this.boundMenuDrawerOpen = this.handleMenuDrawerOpen.bind(this);
    this.boundOnMobileSearchIconClick = this.onMobileSearchIconClick.bind(this);
    this.boundCloseMobileSearchOnScroll = this.closeMobileSearchOnScroll.bind(this);
    this.boundOnMobileSearchFocusChange = this.onMobileSearchFocusChange.bind(this);
    this.boundAutoMobileSearchOnScroll = this.autoMobileSearchOnScroll.bind(this);

    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input?.addEventListener('blur', this.onBrInputBlur.bind(this));
  }

  connectedCallback() {
    document.addEventListener('click', this.boundHandleClickOutside);
    document.addEventListener('pointerdown', this.boundOnBrPointerDown, true);
    document.addEventListener('menu-drawer:open', this.boundMenuDrawerOpen);
    this.mobileSearchTrigger?.addEventListener('click', this.boundOnMobileSearchIconClick);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.boundOnBrViewportResize);
      this.onBrViewportResize();
    }
    if (this.isHeaderSearch) {
      this.addEventListener('focusin', this.boundOnMobileSearchFocusChange);
      this.addEventListener('focusout', this.boundOnMobileSearchFocusChange);
    }
    requestAnimationFrame(() => this.attachAutoMobileSearch());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('pointerdown', this.boundOnBrPointerDown, true);
    document.removeEventListener('menu-drawer:open', this.boundMenuDrawerOpen);
    this.mobileSearchTrigger?.removeEventListener('click', this.boundOnMobileSearchIconClick);
    window.visualViewport?.removeEventListener('resize', this.boundOnBrViewportResize);
    this.removeEventListener('focusin', this.boundOnMobileSearchFocusChange);
    this.removeEventListener('focusout', this.boundOnMobileSearchFocusChange);
    this.detachAutoMobileSearch();
    this.detachMobileSearchScrollClose();
  }

  isMobileHeaderSearchLayout() {
    return window.matchMedia('(max-width: 767.98px)').matches;
  }

  shouldKeepMobileSearchVisible() {
    return (
      Boolean(this.headerSearchWrapper) &&
      this._autoMobileSearchEnabled &&
      this.isMobileHeaderSearchLayout() &&
      window.scrollY <= 48
    );
  }

  attachAutoMobileSearch() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    if (!this.headerSearchWrapper || this._autoMobileSearchEnabled) return;
    this._autoMobileSearchEnabled = true;
    this.setMobileSearchTriggerExpanded(this.headerSearchWrapper.classList.contains('active'));
    window.addEventListener('scroll', this.boundAutoMobileSearchOnScroll, { passive: true });
    this.autoMobileSearchOnScroll();
  }

  detachAutoMobileSearch() {
    if (!this._autoMobileSearchEnabled) return;
    this._autoMobileSearchEnabled = false;
    if (this._autoMobileSearchScrollRaf != null) {
      cancelAnimationFrame(this._autoMobileSearchScrollRaf);
      this._autoMobileSearchScrollRaf = null;
    }
    window.removeEventListener('scroll', this.boundAutoMobileSearchOnScroll, { passive: true });
  }

  autoMobileSearchOnScroll() {
    if (!this._autoMobileSearchEnabled || this._autoMobileSearchScrollRaf != null) return;
    this._autoMobileSearchScrollRaf = requestAnimationFrame(() => {
      this._autoMobileSearchScrollRaf = null;
      const wrapper = this.headerSearchWrapper;
      if (!wrapper || !this.isMobileHeaderSearchLayout()) return;
      if (document.body.classList.contains('search-open')) return;
      const active = document.activeElement;
      if (active && this.contains(active)) return;

      const y = window.scrollY;
      if (y <= 48) {
        wrapper.classList.add('active');
        this.setMobileSearchTriggerExpanded(true);
      } else if (y >= 120) {
        wrapper.classList.remove('active');
        this.setMobileSearchTriggerExpanded(false);
      }
    });
  }

  openMobileHeaderSearchPanel() {
    const wrapper = this.headerSearchWrapper;
    if (!wrapper) return;
    this.setMobileSearchTriggerExpanded(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => wrapper.classList.add('active'));
    });
  }

  handleMenuDrawerOpen() {
    this.detachMobileSearchScrollClose();
    this.classList.remove('predictive-search-open');
    document.body.classList.remove('search-open');
    const wrapper = this.headerSearchWrapper;
    if (wrapper && !this.shouldKeepMobileSearchVisible()) wrapper.classList.remove('active');
    this.setMobileSearchTriggerExpanded(Boolean(wrapper?.classList.contains('active')));
  }

  attachMobileSearchScrollClose() {
    this.detachMobileSearchScrollClose();

    const baselineThenListen = () => {
      if (this._mobileSearchScrollWindowListener) return;
      if (!this.headerSearchWrapper?.classList.contains('active')) return;
      this._mobileSearchOpenedScrollY = window.scrollY;
      this._mobileSearchScrollGraceUntil = Date.now() + 750;
      this._mobileSearchScrollWindowListener = true;
      window.addEventListener('scroll', this.boundCloseMobileSearchOnScroll, { passive: true });
    };

    const wrapper = this.headerSearchWrapper;
    if (wrapper) {
      const onTransitionEnd = (event) => {
        if (event.target !== wrapper) return;
        if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
        this._mobileSearchRemoveTransitionEnd?.();
        clearTimeout(this._mobileSearchScrollFallbackId);
        this._mobileSearchScrollFallbackId = null;
        requestAnimationFrame(baselineThenListen);
      };
      wrapper.addEventListener('transitionend', onTransitionEnd);
      this._mobileSearchRemoveTransitionEnd = () => {
        wrapper.removeEventListener('transitionend', onTransitionEnd);
        this._mobileSearchRemoveTransitionEnd = null;
      };
    }

    this._mobileSearchScrollFallbackId = window.setTimeout(() => {
      this._mobileSearchScrollFallbackId = null;
      this._mobileSearchRemoveTransitionEnd?.();
      baselineThenListen();
    }, 340);
  }

  detachMobileSearchScrollClose() {
    this._mobileSearchScrollGraceUntil = 0;
    clearTimeout(this._mobileSearchScrollFallbackId);
    this._mobileSearchScrollFallbackId = null;
    this._mobileSearchRemoveTransitionEnd?.();
    if (this._mobileSearchScrollRaf != null) {
      cancelAnimationFrame(this._mobileSearchScrollRaf);
      this._mobileSearchScrollRaf = null;
    }
    if (this._mobileSearchScrollWindowListener) {
      this._mobileSearchScrollWindowListener = false;
      window.removeEventListener('scroll', this.boundCloseMobileSearchOnScroll, { passive: true });
    }
  }

  onMobileSearchFocusChange() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    if (!this.headerSearchWrapper?.classList.contains('active')) return;
    this._mobileSearchScrollGraceUntil = Date.now() + 500;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._mobileSearchScrollWindowListener) return;
        this._mobileSearchOpenedScrollY = window.scrollY;
      });
    });
  }

  onBrPointerDown(e) {
    this._brLastPointerDownAt = Date.now();
    this._brLastPointerDownTarget = e.target;
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    const popup = document.getElementById('br-autosuggest');
    if (!popup || !popup.hasAttribute('data-br-focus-trapped')) return;
    if (popup.contains(e.target) || this.input.contains(e.target)) return;
    popup.remove();
  }

  onBrInputBlur() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    const popup = document.getElementById('br-autosuggest');
    if (!popup) return;
    const recentTap = Date.now() - this._brLastPointerDownAt < 400;
    const tappedInside =
      recentTap &&
      this._brLastPointerDownTarget &&
      (popup.contains(this._brLastPointerDownTarget) || this.input.contains(this._brLastPointerDownTarget));
    if (recentTap && !tappedInside) return;
    let trap = popup.querySelector('[data-br-focus-trap]');
    if (!trap) {
      trap = document.createElement('div');
      trap.setAttribute('tabindex', '-1');
      trap.setAttribute('data-br-focus-trap', '');
      trap.setAttribute('aria-hidden', 'true');
      trap.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
      popup.appendChild(trap);
    }
    popup.setAttribute('data-br-focus-trapped', '');
    trap.focus({ preventScroll: true });
  }

  onBrViewportResize() {
    if (!window.visualViewport) return;
    const inset = Math.max(0, Math.round(window.innerHeight - window.visualViewport.height));
    document.documentElement.style.setProperty('--br-keyboard-inset', inset + 'px');
  }

  closeMobileSearchOnScroll() {
    if (this._mobileSearchScrollRaf != null) return;
    this._mobileSearchScrollRaf = requestAnimationFrame(() => {
      this._mobileSearchScrollRaf = null;
      if (!this.headerSearchWrapper?.classList.contains('active')) {
        this.detachMobileSearchScrollClose();
        return;
      }
      if (!this.isMobileHeaderSearchLayout()) return;
      const active = document.activeElement;
      if (active && this.contains(active)) return;
      if (Date.now() < this._mobileSearchScrollGraceUntil) return;
      if (Math.abs(window.scrollY - this._mobileSearchOpenedScrollY) < 72) return;

      this.detachMobileSearchScrollClose();
      this.headerSearchWrapper.classList.remove('active');
      this.setMobileSearchTriggerExpanded(false);
    });
  }

  onMobileSearchIconClick(event) {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    event.preventDefault();
    event.stopPropagation();
    this.openMobileHeaderSearchPanel();
    this.attachMobileSearchScrollClose();
  }

  setMobileSearchTriggerExpanded(isExpanded) {
    this.mobileSearchTrigger?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  clear(event) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
  }

  handleClickOutside(event) {
    const target = event.target;
    const clickedMenuDrawerOverlay =
      target.classList?.contains('fixed-overlay') && target.getAttribute?.('aria-controls') === 'MenuDrawer';
    const isHeaderOverlayClick =
      target.classList?.contains('fixed-overlay') && target.closest('.header-section') && !clickedMenuDrawerOverlay;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) && (Boolean(target.closest('.header__search-close')) || isHeaderOverlayClick)
      : !this.contains(target);

    if (shouldClose) setTimeout(() => this.closeSearch());
  }

  closeSearch() {
    this.classList.remove('predictive-search-open');
    if (!this.isHeaderSearch) return;
    document.body.classList.remove('search-open');
    this.toggleNavigationButton?.classList.add('is-show');
    this.detachMobileSearchScrollClose();
    const wrapper = this.headerSearchWrapper;
    if (wrapper) wrapper.classList.toggle('active', this.shouldKeepMobileSearchVisible());
    this.setMobileSearchTriggerExpanded(Boolean(wrapper?.classList.contains('active')));
    this.input?.blur();
  }
}
customElements.define('predictive-search', PredictiveSearch);
